import http from "../../common/utilities/HttpModule";
import { useCallback, useEffect, useRef } from "react";
import { changeIsTokenRefreshing, logout } from "../store/userSlice";
import { useAppDispatch } from "../../app";
import useAccessTokenRefresh from "./useAccessTokenRefresh";
import useAuthentication from "./useAuthentication";
import useAuth, { isAlternativeFlow } from "../../authProvider/hooks/useAuth";
import {
  ACCESS_TOKEN_COOKIE,
  UNRECOVERABLE_AUTH0_ERRORS,
} from "../../authProvider/constants";
import useAuthenticationTokens from "../../authProvider/hooks/useAuthenticationTokens";

const useAuthAuthenticate = (shouldRun = false, alwaysGetNewToken = false) => {
  const hasFetchedAccessToken = useRef(false);
  const dispatch = useAppDispatch();

  const {
    isAuthenticated,
    getAccessTokenSilently,
    logout: authLogout,
  } = useAuth();

  const { accessToken: injectedAccessToken, removeToken } =
    useAuthenticationTokens();

  const { fetchUserAndSetMetadata } = useAuthentication();

  const handleLogout = useCallback(() => {
    localStorage.removeItem("timeoutId");
    removeToken(ACCESS_TOKEN_COOKIE);
    delete http.axios.defaults.headers.common.Authorization;
    dispatch(logout());
    authLogout();
  }, [authLogout, removeToken, dispatch]);

  const fetchTokenAndMetaData = useCallback(async () => {
    // The approuter owns the session in this flow and injects the token into
    // the page. Auth0 has no session of its own to renew: its silent authorize
    // answers 403, and the SDK then blocks for its full timeout. Every screen
    // that waits on `isTokenRefreshing` stays on its skeleton until then.
    if (isAlternativeFlow) {
      try {
        if (injectedAccessToken) {
          await fetchUserAndSetMetadata(injectedAccessToken, false, true);
          hasFetchedAccessToken.current = true;
        }
      } finally {
        dispatch(changeIsTokenRefreshing(false));
      }
      return;
    }

    if (!isAuthenticated) {
      dispatch(changeIsTokenRefreshing(false));
      return;
    }

    try {
      const accessToken = await getAccessTokenSilently(alwaysGetNewToken);

      if (!accessToken) {
        handleLogout();
        return;
      }

      await fetchUserAndSetMetadata(accessToken, false, true);

      hasFetchedAccessToken.current = true;
    } catch (error) {
      console.error(error);
      // Silent auth can't recover from these (expired Auth0 session, needs
      // interaction/consent). Force an interactive re-login instead of leaving
      // the UI stuck on a misleading "column items" error.
      const errorCode = (error as { error?: string })?.error;
      if (errorCode && UNRECOVERABLE_AUTH0_ERRORS.includes(errorCode)) {
        handleLogout();
      }
    } finally {
      dispatch(changeIsTokenRefreshing(false));
    }
  }, [
    injectedAccessToken,
    alwaysGetNewToken,
    getAccessTokenSilently,
    fetchUserAndSetMetadata,
    isAuthenticated,
    dispatch,
    handleLogout,
  ]);

  useAccessTokenRefresh(shouldRun, fetchUserAndSetMetadata, handleLogout);

  useEffect(() => {
    if (!shouldRun) {
      return;
    }

    if (hasFetchedAccessToken.current) {
      return;
    }

    fetchTokenAndMetaData();
  }, [fetchTokenAndMetaData, shouldRun]);

  return { handleLogout, fetchTokenAndMetaData };
};

export default useAuthAuthenticate;
